/**
 * Union this device's listening history with the account.
 *
 * Failure is a status on the Account screen. It must not block a sit, drop local
 * rows, or throw into the app. Settings are not sent: the Worker accepts a patch,
 * and uploading `mergeStored` would fossilise defaults the way telemetry already did.
 */

import { signal } from '@preact/signals';
import {
  accountSession,
  AuthError,
  postHistorySync,
  renewSessionIfDue,
  storeSession,
} from './session';
import {
  EVENT_ID_RE,
  HISTORY_RETAIN_MS,
  history,
  historyClearedBefore,
  hydrateHistory,
  setHistoryClearedBefore,
  type HistoryRecord,
} from '../state/history';

/** Worker D1 query budget; see worker/auth/model.ts. */
export const MAX_SYNC_EVENTS = 32;

export interface HistoryEvent {
  eventId: string;
  recordingId: string;
  at: number;
  storiesHeard: string[];
  themes: string[];
  completed: boolean;
}

export interface SyncSnapshot {
  history: HistoryEvent[];
  historyClearedBefore: number;
  settingsVersion: number;
  settingsChoices: Record<string, unknown>;
}

export type SyncStatus =
  | { kind: 'idle' }
  | { kind: 'pending' }
  | { kind: 'synced'; at: number }
  | { kind: 'failed'; reason: string };

export const syncStatus = signal<SyncStatus>(
  accountSession.value ? { kind: 'pending' } : { kind: 'idle' },
);

let gate: Promise<void> | null = null;
let again = false;

function normalizeLabels(value: readonly string[]): string[] {
  return [...new Set(value)].sort();
}

export function toHistoryEvent(row: HistoryRecord): HistoryEvent | null {
  if (!EVENT_ID_RE.test(row.eventId)) return null;
  if (!Number.isInteger(row.at) || row.at < 0) return null;
  return {
    eventId: row.eventId,
    recordingId: row.recordingId,
    at: row.at,
    storiesHeard: normalizeLabels(row.storiesHeard),
    themes: normalizeLabels(row.themes),
    completed: row.completed,
  };
}

export function sameHistoryFacts(a: HistoryEvent, b: HistoryEvent): boolean {
  return a.recordingId === b.recordingId && a.at === b.at
    && JSON.stringify(a.storiesHeard) === JSON.stringify(b.storiesHeard)
    && JSON.stringify(a.themes) === JSON.stringify(b.themes);
}

/** Same monotone union the Worker applies. Conflicts keep the stored row. */
export function mergeHistory(
  current: readonly HistoryEvent[],
  incoming: readonly HistoryEvent[],
  clearBefore: number,
  now: number,
): { history: HistoryEvent[]; conflicts: string[] } {
  const cutoff = now - HISTORY_RETAIN_MS;
  const byId = new Map(current
    .filter((event) => event.at > clearBefore && event.at > cutoff)
    .map((event) => [event.eventId, { ...event }]));
  const conflicts: string[] = [];
  for (const event of incoming) {
    if (event.at <= clearBefore || event.at <= cutoff) continue;
    const prior = byId.get(event.eventId);
    if (!prior) {
      byId.set(event.eventId, { ...event });
    } else if (!sameHistoryFacts(prior, event)) {
      conflicts.push(event.eventId);
    } else if (event.completed && !prior.completed) {
      byId.set(event.eventId, { ...prior, completed: true });
    }
  }
  return {
    history: [...byId.values()].sort((a, b) => a.at - b.at
      || a.eventId.localeCompare(b.eventId)),
    conflicts,
  };
}

function asHistoryEvent(value: unknown): HistoryEvent | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const rec = value as Record<string, unknown>;
  if (typeof rec.eventId !== 'string' || !EVENT_ID_RE.test(rec.eventId)
      || typeof rec.recordingId !== 'string'
      || typeof rec.at !== 'number' || !Number.isInteger(rec.at)
      || typeof rec.completed !== 'boolean'
      || !Array.isArray(rec.storiesHeard)
      || !Array.isArray(rec.themes)) return null;
  const storiesHeard = rec.storiesHeard.every((item) => typeof item === 'string')
    ? normalizeLabels(rec.storiesHeard as string[]) : null;
  const themes = rec.themes.every((item) => typeof item === 'string')
    ? normalizeLabels(rec.themes as string[]) : null;
  if (!storiesHeard || !themes) return null;
  return {
    eventId: rec.eventId,
    recordingId: rec.recordingId,
    at: rec.at,
    storiesHeard,
    themes,
    completed: rec.completed,
  };
}

function snapshotFrom(body: Record<string, unknown> | null): SyncSnapshot | null {
  if (!body || !Array.isArray(body.history)
      || typeof body.historyClearedBefore !== 'number') return null;
  const events: HistoryEvent[] = [];
  for (const item of body.history) {
    const event = asHistoryEvent(item);
    if (!event) return null;
    events.push(event);
  }
  return {
    history: events,
    historyClearedBefore: body.historyClearedBefore,
    settingsVersion: typeof body.settingsVersion === 'number' ? body.settingsVersion : 0,
    settingsChoices: body.settingsChoices && typeof body.settingsChoices === 'object'
      && !Array.isArray(body.settingsChoices)
      ? body.settingsChoices as Record<string, unknown>
      : {},
  };
}

function applySnapshot(snapshot: SyncSnapshot): void {
  const now = Date.now();
  const cutoff = now - HISTORY_RETAIN_MS;
  const clearBefore = Math.max(historyClearedBefore(), snapshot.historyClearedBefore);
  setHistoryClearedBefore(clearBefore);
  const local: HistoryEvent[] = [];
  const unsendable: HistoryRecord[] = [];
  for (const row of history.value) {
    const event = toHistoryEvent(row);
    if (event) local.push(event);
    // A row with no id yet — hydrate found no Web Crypto — is still the only copy of
    // that sit. It cannot go to the server, but this snapshot replaces local history
    // wholesale, so leaving it out deletes it as a side effect of a *successful* sync.
    else if (row.at > clearBefore && row.at > cutoff) unsendable.push(row);
  }
  const merged = mergeHistory(snapshot.history, local, clearBefore, now);
  const next: HistoryRecord[] = merged.history.map((event) => ({
    eventId: event.eventId,
    recordingId: event.recordingId,
    at: event.at,
    storiesHeard: event.storiesHeard,
    themes: event.themes,
    completed: event.completed,
  }));
  history.value = [...next, ...unsendable]
    .sort((a, b) => a.at - b.at || a.eventId.localeCompare(b.eventId));
}

function failReason(error: unknown): string {
  if (error instanceof AuthError) return error.message;
  return 'History could not be sent. Try again.';
}

async function runSync(): Promise<void> {
  if (!accountSession.value) {
    syncStatus.value = { kind: 'idle' };
    return;
  }
  syncStatus.value = { kind: 'pending' };
  try {
    await hydrateHistory();
    await renewSessionIfDue();
    const clearBefore = historyClearedBefore();
    const events: HistoryEvent[] = [];
    for (const row of history.value) {
      const event = toHistoryEvent(row);
      if (event && event.at > clearBefore) events.push(event);
    }
    events.sort((a, b) => a.at - b.at || a.eventId.localeCompare(b.eventId));

    let snapshot: SyncSnapshot | null = null;
    const chunks = events.length === 0 ? [[]] : chunk(events, MAX_SYNC_EVENTS);
    for (let i = 0; i < chunks.length; i++) {
      const { status, body } = await postHistorySync({
        history: chunks[i],
        historyClearedBefore: i === 0 ? clearBefore : undefined,
      });
      if (status === 401) {
        storeSession(null);
        throw new AuthError('invalid_session', 401,
          'Sign in again. This device is no longer signed in.');
      }
      if (status === 409 && body && body.error === 'history_event_conflict') {
        const current = snapshotFrom(body.current as Record<string, unknown> ?? body);
        if (current) applySnapshot(current);
        throw new AuthError('history_event_conflict', 409,
          'A sit could not be merged because it collided with one already on the account. Try Sync now again.');
      }
      if (status < 200 || status >= 300) {
        throw new AuthError(
          typeof body?.error === 'string' ? body.error : 'http_error',
          status,
          typeof body?.message === 'string'
            ? body.message
            : 'History could not be sent. Try again.',
        );
      }
      snapshot = snapshotFrom(body);
      if (!snapshot) {
        throw new AuthError('invalid_json', status,
          'The server sent a response this app could not read.');
      }
      applySnapshot(snapshot);
    }
    syncStatus.value = { kind: 'synced', at: Date.now() };
  } catch (error) {
    if (!accountSession.value) {
      syncStatus.value = { kind: 'idle' };
      return;
    }
    syncStatus.value = { kind: 'failed', reason: failReason(error) };
  }
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export function syncHistory(): Promise<void> {
  again = true;
  if (!gate) {
    gate = (async () => {
      try {
        while (again) {
          again = false;
          await runSync();
        }
      } finally {
        gate = null;
      }
    })();
  }
  return gate;
}
